import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  HrUser,
  Leave,
  SubmitLeavePayload,
  LeaveStatus,
  LeaveTypeConfig,
  CreateLeaveTypePayload,
  UpdateLeaveTypePayload,
  LeaveBalance,
  AllocateBalancePayload,
  BulkAllocatePayload,
  Holiday,
  CreateHolidayPayload,
  UpdateHolidayPayload,
} from '../types';

// ─── Users (Team & Access) ────────────────────────────────────────────────

/**
 * People directory for HR screens (leave calendar, pickers). Served by the app's own
 * `/users` endpoint; create / edit / Manage Access live on the Company People page.
 */
export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async (): Promise<HrUser[]> => {
      const { data } = await api.get<{ data?: Array<Record<string, any>> }>('/api/users');
      return (data.data ?? []).map((u) => ({
        id: String(u.id ?? u._id),
        username: u.username ?? '',
        display_name: u.name ?? u.username ?? '',
        role: u.role ?? 'MEMBER',
        department: u.department,
        active: u.status ? u.status !== 'INACTIVE' : true,
        created_at: u.createdAt,
      }));
    },
    placeholderData: keepPreviousData,
  });
}

// ─── Leaves ──────────────────────────────────────────────────────────────

export function useLeaves() {
  return useQuery({
    queryKey: ['leaves'],
    queryFn: async () => {
      const { data } = await api.get<{ leaves: Leave[] }>('/api/leaves');
      return data.leaves ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useLeaveMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['leaves'] });
    queryClient.invalidateQueries({ queryKey: ['leave-balances'] });
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  };

  const submitLeave = useMutation({
    mutationFn: async (payload: SubmitLeavePayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/leaves', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const reviewLeave = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Extract<LeaveStatus, 'approved' | 'rejected'> }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/leaves/${id}`, { status });
      return data;
    },
    onSuccess: invalidate,
  });

  const cancelLeave = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const { data } = await api.post<{ ok: boolean }>(`/api/leaves/${id}/cancel`, { reason });
      return data;
    },
    onSuccess: invalidate,
  });

  return { submitLeave, reviewLeave, cancelLeave };
}

// ─── Leave Types ─────────────────────────────────────────────────────────

export function useLeaveTypes(includeInactive = false) {
  return useQuery({
    queryKey: ['leave-types', includeInactive],
    queryFn: async () => {
      const params = includeInactive ? '?all=true' : '';
      const { data } = await api.get<{ leaveTypes: LeaveTypeConfig[] }>(`/api/leave-types${params}`);
      return data.leaveTypes ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useLeaveTypeMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['leave-types'] });

  const createLeaveType = useMutation({
    mutationFn: async (payload: CreateLeaveTypePayload) => {
      const { data } = await api.post<{ ok: boolean; id: string; code: string }>('/api/leave-types', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateLeaveType = useMutation({
    mutationFn: async ({ code, payload }: { code: string; payload: UpdateLeaveTypePayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/leave-types/${code}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteLeaveType = useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/leave-types/${code}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createLeaveType, updateLeaveType, deleteLeaveType };
}

// ─── Leave Balances ──────────────────────────────────────────────────────

export interface ApplyPolicyResult {
  ok: boolean;
  year: number;
  mode: 'fill' | 'reapply';
  people: number;
  created: number;
  updated: number;
  kept: number;
}

/** Give everyone this year's balance from the leave policy (and optionally bring existing ones in line). */
export function useApplyLeavePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { year?: number; mode?: 'fill' | 'reapply'; include_manual?: boolean }) => {
      const { data } = await api.post<ApplyPolicyResult>('/api/leave-balances/apply-policy', payload);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leave-balances'] }),
  });
}

export function useLeaveBalances(year?: number) {
  const targetYear = year || new Date().getFullYear();
  return useQuery({
    queryKey: ['leave-balances', targetYear],
    queryFn: async () => {
      const { data } = await api.get<{ balances: LeaveBalance[]; year: number }>(`/api/leave-balances?year=${targetYear}`);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useAllLeaveBalances(year?: number, department?: string) {
  const targetYear = year || new Date().getFullYear();
  return useQuery({
    queryKey: ['leave-balances-all', targetYear, department],
    queryFn: async () => {
      let url = `/api/leave-balances?all=true&year=${targetYear}`;
      if (department) url += `&department=${department}`;
      const { data } = await api.get<{ balances: LeaveBalance[]; year: number }>(url);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useLeaveBalanceMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['leave-balances'] });

  const allocate = useMutation({
    mutationFn: async (payload: AllocateBalancePayload) => {
      const { data } = await api.post<{ ok: boolean }>('/api/leave-balances/allocate', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const bulkAllocate = useMutation({
    mutationFn: async (payload: BulkAllocatePayload) => {
      const { data } = await api.post<{ ok: boolean; count: number }>('/api/leave-balances/bulk-allocate', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  return { allocate, bulkAllocate };
}

// ─── Holidays ────────────────────────────────────────────────────────────

export function useHolidays(year?: number) {
  const targetYear = year || new Date().getFullYear();
  return useQuery({
    queryKey: ['holidays', targetYear],
    queryFn: async () => {
      const { data } = await api.get<{ holidays: Holiday[]; year: number }>(`/api/holidays?year=${targetYear}`);
      return data.holidays ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useHolidayMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['holidays'] });

  const createHoliday = useMutation({
    mutationFn: async (payload: CreateHolidayPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/holidays', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateHoliday = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateHolidayPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/holidays/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteHoliday = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/holidays/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createHoliday, updateHoliday, deleteHoliday };
}
